import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { IntakeSectionHeader } from "@/common/styles";
import { Col, Row } from "antd";
import { FormContainer } from "../InmateDetails/styles";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import {
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import CommonDatePicker from "@/common/elements/DatePicker";
import { useSelector } from "react-redux";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";

const SentencingInformation = ({
  releaseDateOverridden,
  sentenceConsecutive,
  sentenceLength,
  useEHM,
  ehm,
  useAdditionalGoodTimeDays,
  startingPoint = "",
}) => {
  const { bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const fullSearch = useSelector((state) => state.fullSearch);

  return (
    <FieldSecurityProvider
      entity={SCHEMA_NAME.CHARGES}
      startingPoint={startingPoint}
      bookingsFieldsEnabled={bookingsFieldsEnabled}
    >
      <>
        <IntakeSectionHeader>
          <span>Sentencing Information</span>
        </IntakeSectionHeader>

        <FormContainer>
          <Row gutter={16}>
            <Col span={8}>
              <SecureFormItem
                fieldName="sentence"
                formItemProps={{
                  name: "sentence",
                  label: "Sentence Description",
                  layout: "vertical",
                }}
              >
                <TextField />
              </SecureFormItem>
            </Col>

            <Col span={8}>
              <SecureFormItem
                fieldName="sentenceDate"
                formItemProps={{
                  name: "sentenceDate",
                  label: "Sentence Date",
                  layout: "vertical",
                }}
              >
                <CommonDatePicker
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  showTime={false}
                />
              </SecureFormItem>
            </Col>

            <Col span={8}>
              <SecureFormItem
                fieldName="sentencingCountyID"
                formItemProps={{
                  name: "sentencingCountyID",
                  label: "Sentencing County",
                  layout: "vertical",
                }}
              >
                <Select
                  options={getOptionsFromRefList(
                    "Sentencing_County",
                    false,
                    false,
                    true
                  )}
                  placeholder="Sentencing County"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </Col>
          </Row>

          {/* Electronic Home Monitoring — only shown when the client param
              Bookings\UseEHM is enabled, mirroring the legacy ASP app's
              "tricounty" EHM section. EHM Days only appears once EHM? is
              checked. */}
          {useEHM && (
            <Row gutter={16}>
              <Col span={5} style={{ display: "flex", alignItems: "center" }}>
                <SecureFormItem
                  fieldName="ehm"
                  formItemProps={{
                    name: "ehm",
                    valuePropName: "checked",
                    label: "EHM?",
                    layout: "vertical",
                  }}
                >
                  <Checkbox style={{ flex: 1 }} />
                </SecureFormItem>
              </Col>

              {ehm && (
                <Col span={8}>
                  <SecureFormItem
                    fieldName="ehmDays"
                    formItemProps={{
                      name: "ehmDays",
                      label: "EHM Days",
                      layout: "vertical",
                    }}
                  >
                    <TextField type="number" />
                  </SecureFormItem>
                </Col>
              )}
            </Row>
          )}

          <Row gutter={16}>
            <Col span={5} style={{ display: "flex", alignItems: "center" }}>
              <SecureFormItem
                fieldName="sentenceConsecutive"
                formItemProps={{
                  name: "sentenceConsecutive",
                  valuePropName: "checked",
                  label: "Sentence Consecutive?",
                  layout: "vertical",
                }}
              >
                <Checkbox style={{ flex: 1 }} />
              </SecureFormItem>
            </Col>

            <Col span={8}>
              <SecureFormItem
                fieldName="sentenceConsecutiveTo"
                formItemProps={{
                  name: "sentenceConsecutiveTo",
                  label: "To (Sequence #)",
                  layout: "vertical",
                }}
                overrideDisabled={!sentenceConsecutive && !fullSearch}
              >
                <TextField />
              </SecureFormItem>
            </Col>

            <Col span={8}>
              <SecureFormItem
                fieldName="sentenceStartDateTime"
                formItemProps={{
                  name: "sentenceStartDateTime",
                  label: "Sentence Start Date & Time",
                  layout: "vertical",
                }}
                overrideDisabled={sentenceConsecutive}
              >
                <CommonDatePicker
                  placeholder="MM/DD/YYYY HH:mm"
                  format="MM/DD/YYYY HH:mm"
                />
              </SecureFormItem>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <SecureFormItem
                fieldName="sentenceLength"
                formItemProps={{
                  name: "sentenceLength",
                  label: "Sentence Length",
                  layout: "vertical",
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>

            <Col span={6}>
              <SecureFormItem
                fieldName="stayedOrSuspendedDays"
                formItemProps={{
                  name: "stayedOrSuspendedDays",
                  label: "Stayed/Suspended Days",
                  layout: "vertical",
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>

            <Col span={6}>
              <SecureFormItem
                fieldName="treatmentDays"
                formItemProps={{
                  name: "treatmentDays",
                  label: "Treatment Days",
                  layout: "vertical",
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>

            <Col span={6}>
              <SecureFormItem
                fieldName="creditDaysServed"
                formItemProps={{
                  name: "creditDaysServed",
                  label: "Credit For Days Served",
                  layout: "vertical",
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <SecureFormItem
                fieldName="goodTimeDays"
                formItemProps={{
                  name: "goodTimeDays",
                  label: "Good Time Days",
                  layout: "vertical",
                }}
                overrideDisabled={!fullSearch}
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>

            <Col span={8}>
              <SecureFormItem
                fieldName="goodTimeDaysLost"
                formItemProps={{
                  name: "goodTimeDaysLost",
                  label: "Lost Good Time Days",
                  layout: "vertical",
                }}
                overrideDisabled={
                  !isNonEmptyVariable(sentenceLength) ||
                  parseInt(sentenceLength) <= 9
                }
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>

            {useAdditionalGoodTimeDays && (
              <Col span={8}>
                <SecureFormItem
                  fieldName="additionalGoodTimeDays"
                  formItemProps={{
                    name: "additionalGoodTimeDays",
                    label: "Additional Good Time Days",
                    layout: "vertical",
                  }}
                  overrideDisabled={
                    !isNonEmptyVariable(sentenceLength) ||
                    parseInt(sentenceLength) <= 9
                  }
                >
                  <TextField type="number" />
                </SecureFormItem>
              </Col>
            )}

            <Col span={8}>
              <SecureFormItem
                fieldName="totalDaysForThisCharge"
                formItemProps={{
                  name: "totalDaysForThisCharge",
                  label: "Total Days for this Charge",
                  layout: "vertical",
                }}
                overrideDisabled={!fullSearch}
              >
                <TextField type="number" />
              </SecureFormItem>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <SecureFormItem
                fieldName="sentenceEndDate"
                formItemProps={{
                  name: "sentenceEndDate",
                  label: "End Date",
                  layout: "vertical",
                }}
                overrideDisabled={!fullSearch}
              >
                <CommonDatePicker
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  showTime={false}
                />
              </SecureFormItem>
            </Col>

            <Col span={6} style={{ display: "flex", alignItems: "center" }}>
              <SecureFormItem
                fieldName="releaseDateOverridden"
                formItemProps={{
                  name: "releaseDateOverridden",
                  valuePropName: "checked",
                  label: "Override Release Date?",
                  layout: "vertical",
                }}
              >
                <Checkbox style={{ flex: 1 }} />
              </SecureFormItem>
            </Col>

            <Col span={8}>
              <SecureFormItem
                fieldName="scheduledReleaseDateTime"
                formItemProps={{
                  name: "scheduledReleaseDateTime",
                  label: "Scheduled Release Date & Time",
                  layout: "vertical",
                }}
                overrideDisabled={!releaseDateOverridden && !fullSearch}
              >
                <CommonDatePicker
                  placeholder="MM/DD/YYYY HH:mm"
                  format="MM/DD/YYYY HH:mm"
                />
              </SecureFormItem>
            </Col>
          </Row>
        </FormContainer>
      </>
    </FieldSecurityProvider>
  );
};

export default SentencingInformation;